import { EUserStatus } from '../../types/newReview/userStatus';
import { ENewReviewFlow, ENewReviewMode, ENewReviewStep } from '../../types/newReview';
import { TReduxActions } from '../../types/redux';
import { ITripInfo } from '../../repositories/dailyrent/entities/response/TripInfo';
import {
  EAnonymousActionTypes,
  EConsActionTypes,
  ENameActionTypes,
  ENewReviewForm,
  EOpenNewReviewForm,
  EOverallScoreActionTypes,
  EProsActionTypes,
  EStepActionTypes,
  EUserStatusActionTypes,
} from '../../actions/newReview';
import { EFlowActionTypes } from '../../actions/newReview/flowActionTypes';
import { EPoliteScoreActionTypes } from '../../actions/newReview/politeScoreActions';
import { EPriceAndQualityScoreActionTypes } from '../../actions/newReview/priceAndQuality';
import { ELocationScoreActionTypes } from '../../actions/newReview/locationScoreActions';
import { EComfortAndApplianceScoreActionTypes } from '../../actions/newReview/comfortAndApplianceScoreActions';
import { ECleaningScoreActionTypes } from '../../actions/newReview/cleaningScoreActions';
import { ENewReviewPublished } from '../../actions/newReview/publicshedActionTypes';
import { getFirstStep, getFormMode, getNextStep, getPrevStep } from '../../utils/newReview/steps';
import { EStartReviewErrorCode } from '../../types/newReview/startReviewErrorCodes';
import { ECreateReviewDataActionTypes } from '../../actions/newReview/pageDataActionTypes';
import { ENewReviewLoading } from '../../actions/newReview/loadingActionTypes';
import { ENewReviewLoadingError } from '../../actions/newReview/loadingErrorActionTypes';

interface IDefaultNewReviewProps {
  openByDefault?: boolean;
  rejectAccessError?: EStartReviewErrorCode | null;
  tripInfo?: ITripInfo | null;
  tripId?: number | null;
  flow?: ENewReviewFlow | null;
}

export const defaultNewReview = ({
  openByDefault,
  rejectAccessError = null,
  tripInfo = null,
  tripId = null,
  flow = null,
}: IDefaultNewReviewProps): INewReviewReducerState => {
  const mode = getFormMode(flow);

  return {
    meta: {
      publishedReviewId: null,
      loading: false,
      loadingError: false,
      open: false,
      shouldAutoOpen: !!openByDefault,
      mode,
      flow,
      step: getFirstStep(mode),
      rejectAccessError,
      tripInfo,
      tripId,
    },
    data: {
      userStatus: EUserStatus.Seeker,
      pros: null,
      cons: null,
      name: null,
      isAnonymous: null,

      overallScore: 0,
      polite: 0,
      priceAndQuality: 0,
      location: 0,
      comfortAndAppliance: 0,
      cleaning: 0,
    },
  };
};

export interface INewReviewReducerState {
  meta: {
    publishedReviewId: number | null;
    loading: boolean;
    loadingError: boolean;
    open: boolean;
    shouldAutoOpen: boolean;
    mode: ENewReviewMode;
    flow: ENewReviewFlow | null;
    step: ENewReviewStep;
    rejectAccessError: EStartReviewErrorCode | null | undefined;
    tripInfo: ITripInfo | null;
    tripId: number | null;
  };
  data: {
    userStatus: EUserStatus;
    pros: string | null;
    cons: string | null;
    name: string | null;
    isAnonymous: boolean | null;

    overallScore: number;
    polite: number;
    priceAndQuality: number;
    location: number;
    comfortAndAppliance: number;
    cleaning: number;
  };
}

export const defaultState: INewReviewReducerState = {
  meta: {
    publishedReviewId: null,
    loading: false,
    loadingError: false,
    open: false,
    shouldAutoOpen: false,
    mode: ENewReviewMode.Full,
    flow: null,
    step: getFirstStep(ENewReviewMode.Full),
    rejectAccessError: null,
    tripInfo: null,
    tripId: null,
  },
  data: {
    userStatus: EUserStatus.Seeker,
    pros: null,
    cons: null,
    name: null,
    isAnonymous: null,

    overallScore: 0,
    polite: 0,
    priceAndQuality: 0,
    location: 0,
    comfortAndAppliance: 0,
    cleaning: 0,
  },
};

export const newReviewReducer = (state = defaultState, action: TReduxActions): INewReviewReducerState => {
  switch (action.type) {
    // Create Review Data Actions
    case ECreateReviewDataActionTypes.Start: {
      return { ...state, meta: { ...state.meta, loading: true, loadingError: false } };
    }
    case ECreateReviewDataActionTypes.Success: {
      const mode = getFormMode(action.payload.flow);

      return {
        ...state,
        meta: {
          ...state.meta,
          tripInfo: action.payload.tripInfo,
          mode: getFormMode(action.payload.flow),
          flow: action.payload.flow,
          step: getFirstStep(mode),
          rejectAccessError: null,
          loading: false,
        },
      };
    }
    case ECreateReviewDataActionTypes.Error: {
      return {
        ...state,
        meta: {
          ...state.meta,
          rejectAccessError: action.payload,
          loadingError: true,
          loading: false,
        },
      };
    }

    // Loading Actions (для совместимости с другими экшенами, например addReview)
    case ENewReviewLoading.Set: {
      return { ...state, meta: { ...state.meta, loading: action.payload } };
    }
    case ENewReviewLoadingError.Set: {
      return { ...state, meta: { ...state.meta, loadingError: action.payload } };
    }

    // Modal Actions
    case EOpenNewReviewForm.Open: {
      return { ...state, meta: { ...state.meta, open: true } };
    }
    case EOpenNewReviewForm.Close: {
      return { ...state, meta: { ...state.meta, open: false } };
    }

    // Step Actions
    case EStepActionTypes.Next: {
      return { ...state, meta: { ...state.meta, step: getNextStep(state.meta.step, state.meta.mode) } };
    }
    case EStepActionTypes.Prev: {
      return { ...state, meta: { ...state.meta, step: getPrevStep(state.meta.step, state.meta.mode) } };
    }
    case EStepActionTypes.Reset: {
      return { ...state, meta: { ...state.meta, step: getFirstStep(state.meta.mode) } };
    }

    // Flow Actions
    case EFlowActionTypes.Set: {
      return { ...state, meta: { ...state.meta, flow: action.payload } };
    }

    // Publish Actions
    case ENewReviewPublished.Set: {
      return { ...state, meta: { ...state.meta, publishedReviewId: action.payload } };
    }
    case EOverallScoreActionTypes.SetRating: {
      return { ...state, data: { ...state.data, overallScore: action.payload } };
    }
    case EPoliteScoreActionTypes.SetRating: {
      return { ...state, data: { ...state.data, polite: action.payload } };
    }
    case EPriceAndQualityScoreActionTypes.SetRating: {
      return { ...state, data: { ...state.data, priceAndQuality: action.payload } };
    }
    case ELocationScoreActionTypes.SetRating: {
      return { ...state, data: { ...state.data, location: action.payload } };
    }
    case EComfortAndApplianceScoreActionTypes.SetRating: {
      return { ...state, data: { ...state.data, comfortAndAppliance: action.payload } };
    }
    case ECleaningScoreActionTypes.SetRating: {
      return { ...state, data: { ...state.data, cleaning: action.payload } };
    }

    case EUserStatusActionTypes.SetUserStatus: {
      return { ...state, data: { ...state.data, userStatus: action.payload } };
    }
    case EProsActionTypes.Set: {
      return { ...state, data: { ...state.data, pros: action.payload } };
    }
    case EConsActionTypes.Set: {
      return { ...state, data: { ...state.data, cons: action.payload } };
    }
    case ENameActionTypes.SetName: {
      return { ...state, data: { ...state.data, name: action.payload } };
    }
    case EAnonymousActionTypes.Set: {
      return { ...state, data: { ...state.data, isAnonymous: action.payload } };
    }
    case ENewReviewForm.Clear: {
      return { ...defaultState };
    }
    case ENewReviewForm.ClearData: {
      return { ...state, data: defaultState.data };
    }

    default: {
      return state;
    }
  }
};
